import React, { useRef, useState } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Target, TrendingUp, Megaphone, PenTool, Users, Monitor, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';
import Layout from '../components/layout/Layout';
import SEOHead from '../components/SEOHead';

const TextReveal = ({ children, delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-60px" });
  return (
    <div ref={ref} className="overflow-hidden">
      <motion.div initial={{ y: "100%", opacity: 0 }} animate={isInView ? { y: 0, opacity: 1 } : {}} transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}>
        {children}
      </motion.div>
    </div>
  );
};

const FAQItem = ({ q, a, open, onClick }) => (
  <div className="border-b border-[#FF6B35]/10 last:border-0">
    <button onClick={onClick} className="w-full py-5 flex items-center justify-between text-left group" aria-expanded={open}>
      <span className="text-[#2D2D2D] font-medium pr-4 group-hover:text-[#FF6B35] transition-colors">{q}</span>
      {open ? <ChevronUp size={18} className="text-[#FF6B35] flex-shrink-0" /> : <ChevronDown size={18} className="text-[#8A8A8A] flex-shrink-0" />}
    </button>
    <motion.div initial={false} animate={{ height: open ? 'auto' : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
      <p className="pb-5 text-[#5A5A5A] text-sm leading-relaxed">{a}</p>
    </motion.div>
  </div>
);

const Digital = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const services = [
    { icon: Target, title: 'Brand Strategy', desc: 'We define your market position, brand voice, visual identity, and competitive differentiation. Every campaign, every asset, every touchpoint starts here.', link: '/services/brand-strategy', points: ['Brand audit & positioning', 'Visual identity systems', 'Market differentiation', 'Messaging framework'] },
    { icon: TrendingUp, title: 'Performance Marketing', desc: 'Data-driven campaigns on Google, Meta, and LinkedIn designed to deliver qualified leads and measurable ROI.', link: '/services/performance-marketing', points: ['Google & Meta Ads', 'Conversion tracking', 'A/B testing frameworks', 'Weekly optimization cycles'] },
    { icon: Megaphone, title: 'Social Media Management', desc: 'Content calendars, community building, and platform-specific strategies for consistent brand presence.', link: '/services/social-media-management', points: ['Platform strategy', 'Content calendar', 'Community management', 'Growth analytics'] },
    { icon: PenTool, title: 'Content Production', desc: 'SEO-optimized blogs, video scripts, and creative assets that position you as an authority.', link: '/services/content-production', points: ['SEO blog writing', 'Video content strategy', 'Brand copywriting', 'Visual content'] },
    { icon: Users, title: 'Founder Branding', desc: 'Personal brand positioning for founders and CXOs. LinkedIn growth, thought leadership, and media positioning.', link: '/services/founder-branding', points: ['LinkedIn optimization', 'Thought leadership', 'Personal content strategy', 'Authority building'] },
    { icon: Monitor, title: 'Website Design', desc: 'Conversion-focused websites built on modern tech stacks. Fast, responsive, and SEO-optimized.', link: '/services/website-design', points: ['UI/UX design', 'Responsive development', 'SEO architecture', 'Speed optimization'] },
  ];

  const process = [
    { step: '01', title: 'Discovery', desc: 'We audit your current presence, study your market, and understand your goals before proposing anything.' },
    { step: '02', title: 'Strategy', desc: 'A custom growth roadmap with clear milestones, channel priorities, and budget allocation.' },
    { step: '03', title: 'Execution', desc: 'Our specialists build, launch, and manage campaigns across every channel in your strategy.' },
    { step: '04', title: 'Optimize', desc: 'Weekly data reviews, A/B tests, and strategic pivots to maximize performance and ROI.' },
  ];

  const faqs = [
    { q: 'What industries do you specialize in?', a: 'We work with D2C brands, SaaS startups, real estate developers, hospitality businesses, professional services, and e-commerce companies across India, Dubai, and the UAE.' },
    { q: 'How is BCM different from a typical digital agency?', a: 'We are a creative consultancy, not a task factory. We start with strategy, not tactics. Every campaign connects back to your brand positioning and business objectives.' },
    { q: 'What is your pricing model?', a: 'We offer project-based pricing and monthly retainers depending on scope. Every engagement starts with a discovery call to understand your needs before we propose a solution.' },
    { q: 'Do you work with early-stage startups?', a: 'Yes. We have specific packages for founders who need brand identity, website, and initial growth marketing. We scale our involvement as you grow.' },
    { q: 'How long before we see results from performance marketing?', a: 'Paid campaigns show initial data within 2-3 weeks. Optimized performance typically peaks around week 6-8. SEO and content strategies compound over 3-6 months.' },
  ];

  return (
    <Layout>
      <SEOHead
        title="Digital Marketing & Brand Strategy | Buzz Craft Maven"
        description="Strategic digital marketing, brand strategy, performance marketing, social media management, and website design for modern businesses. BCM is a creative consultancy based in India and Dubai."
        canonical="https://buzzcraftmaven.com/digital"
        schemas={[
          { "@context": "https://schema.org", "@type": "Service", "name": "Digital Marketing Solutions", "provider": { "@type": "Organization", "name": "Buzz Craft Maven" }, "description": "Strategic digital marketing, brand strategy, and growth services.", "areaServed": ["India", "Dubai", "UAE"] },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buzzcraftmaven.com" }, { "@type": "ListItem", "position": 2, "name": "Digital Solutions", "item": "https://buzzcraftmaven.com/digital" }] },
          { "@context": "https://schema.org", "@type": "FAQPage", "mainEntity": faqs.map(f => ({ "@type": "Question", "name": f.q, "acceptedAnswer": { "@type": "Answer", "text": f.a } })) }
        ]}
      />

      {/* Hero */}
      <section className="pt-32 pb-16 md:pt-40 md:pb-24 bg-sunrise-hero relative overflow-hidden">
        <div className="absolute top-10 right-[10%] w-[350px] h-[350px] sun-orb sun-orb-primary opacity-30" />
        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <nav className="text-sm text-[#8A8A8A] mb-8" aria-label="Breadcrumb"><Link to="/" className="hover:text-[#FF6B35]">Home</Link> <span className="mx-2">/</span> <span className="text-[#2D2D2D]">Digital Solutions</span></nav>
          <div className="max-w-3xl">
            <TextReveal><h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2D2D2D] tracking-tight leading-[1.05] mb-6">Digital Marketing That <span className="text-gradient-sunrise">Drives Growth.</span></h1></TextReveal>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }} className="text-base md:text-lg text-[#5A5A5A] max-w-xl leading-relaxed mb-8">
              From brand strategy to performance campaigns — we build digital engines that generate leads, build authority, and scale revenue for modern businesses.
            </motion.p>
            <Link to="/contact"><motion.button className="btn-sunrise px-8 py-3.5" whileHover={{ scale: 1.02 }}><span className="flex items-center gap-2">Get a Strategy Call <ArrowRight size={18} /></span></motion.button></Link>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="Digital services">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-12">What We <span className="text-gradient-sunrise">Deliver.</span></h2></TextReveal>
          <div className="space-y-6">
            {services.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="p-6 md:p-8 bg-white rounded-2xl border border-[#FF6B35]/8 hover:border-[#FF6B35]/20 transition-all" data-testid={`digital-service-detail-${i}`}>
                <div className="flex flex-col md:flex-row gap-6">
                  <div className="w-12 h-12 rounded-xl bg-[#FF6B35]/8 flex items-center justify-center flex-shrink-0">
                    <s.icon size={22} className="text-[#FF6B35]" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-display text-xl font-semibold text-[#2D2D2D] mb-2">{s.title}</h3>
                    <p className="text-[#5A5A5A] text-sm leading-relaxed mb-4">{s.desc}</p>
                    <div className="grid grid-cols-2 gap-2 mb-4">
                      {s.points.map(p => (
                        <div key={p} className="flex items-center gap-2 text-sm text-[#5A5A5A]">
                          <CheckCircle size={14} className="text-[#FF6B35] flex-shrink-0" /> {p}
                        </div>
                      ))}
                    </div>
                    {s.link && <Link to={s.link} className="text-[#FF6B35] text-sm font-medium hover:underline inline-flex items-center gap-1">Learn more <ArrowRight size={14} /></Link>}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20 md:py-28 bg-white" aria-label="Our process">
        <div className="container mx-auto px-6 md:px-12">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-12">How We <span className="text-gradient-sunrise">Work.</span></h2></TextReveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((p, i) => (
              <motion.div key={p.step} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative p-6 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/8">
                <span className="font-display text-4xl font-bold text-[#FF6B35]/10 absolute top-3 right-4">{p.step}</span>
                <h3 className="font-semibold text-[#2D2D2D] mb-2 mt-2">{p.title}</h3>
                <p className="text-[#5A5A5A] text-sm">{p.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 md:py-28 bg-[#FFFBF5]" aria-label="FAQ">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <TextReveal><h2 className="font-display text-3xl md:text-4xl text-[#2D2D2D] mb-10 text-center">Questions About <span className="text-gradient-sunrise">Digital Services</span></h2></TextReveal>
          <div className="bg-white rounded-2xl p-6 md:p-8 border border-[#FF6B35]/8">
            {faqs.map((f, i) => <FAQItem key={i} q={f.q} a={f.a} open={openFaq === i} onClick={() => setOpenFaq(openFaq === i ? null : i)} />)}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#FF6B35] to-[#FFB627] text-center">
        <div className="container mx-auto px-6 md:px-12">
          <h2 className="font-display text-3xl md:text-4xl text-white mb-4">Ready to grow your brand digitally?</h2>
          <p className="text-white/80 max-w-md mx-auto mb-8">Book a free strategy call and let&apos;s map out your growth roadmap.</p>
          <Link to="/contact"><motion.button className="px-10 py-4 bg-white text-[#FF6B35] rounded-full font-semibold shadow-xl hover:shadow-2xl transition-shadow inline-flex items-center gap-2" whileHover={{ scale: 1.02 }}>Book a Strategy Call <ArrowRight size={18} /></motion.button></Link>
        </div>
      </section>
    </Layout>
  );
};

export default Digital;
